import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { API, useAuth } from "../AuthContext";
import AppHeader from "../components/AppHeader";
import SiteFooter from "../components/SiteFooter";

const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 12;

export default function BillingSuccess() {
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | failed | expired
  const [planLabel, setPlanLabel] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const { data } = await axios.get(`${API}/billing/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPlanLabel(data.plan);
          setStatus("paid");
          await refreshUser();
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
      } catch (e) {
        // ignore — keep polling
      }
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        setStatus("failed");
        return;
      }
      setTimeout(poll, POLL_INTERVAL);
    };

    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [sessionId]);

  return (
    <div className="page" data-testid="billing-success-page">
      <div className="grain-bg" />
      <div className="container-narrow">
        <AppHeader />

        <div className="fade-in" style={{ paddingTop: "6vh" }}>
          {status === "checking" && (
            <>
              <div className="eyebrow shimmer" style={{ marginBottom: 16 }}>Confirming your payment</div>
              <h1 className="display">One moment…</h1>
              <p className="serif-italic" style={{ fontSize: 17, color: "var(--accent)", lineHeight: 1.7 }}>
                We're checking with Stripe. This usually takes a few seconds.
              </p>
            </>
          )}

          {status === "paid" && (
            <>
              <div className="eyebrow" style={{ marginBottom: 16 }}>Welcome to the membership</div>
              <h1 className="display" data-testid="billing-success-heading">
                You're in.
              </h1>
              <p className="serif-italic" style={{ fontSize: 18, color: "var(--accent)", lineHeight: 1.7, marginBottom: 32 }}>
                {planLabel === "yearly"
                  ? "A full year of unlimited reframes is on the table. Drop in as many thoughts as you carry."
                  : "Unlimited reframes for the month. Drop in as many thoughts as you carry."}
              </p>
              <button className="btn-primary" onClick={() => navigate("/app")} data-testid="billing-success-cta">
                Open the app
              </button>
            </>
          )}

          {(status === "failed" || status === "expired") && (
            <>
              <div className="eyebrow" style={{ marginBottom: 16 }}>Something's off</div>
              <h1 className="display" data-testid="billing-fail-heading">
                We couldn't confirm the payment.
              </h1>
              <p className="serif-italic" style={{ fontSize: 17, color: "var(--accent)", lineHeight: 1.7, marginBottom: 32 }}>
                If you completed checkout, your subscription will activate automatically once Stripe confirms. Otherwise, try again.
              </p>
              <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                <Link to="/upgrade" className="btn-primary" data-testid="billing-retry-btn">
                  Try again
                </Link>
                <Link to="/app" className="btn-ghost">Back to app</Link>
              </div>
            </>
          )}
        </div>
        <SiteFooter />
      </div>
    </div>
  );
}
